import React, { useState, useEffect } from 'react';
import { Select, Text } from "@geist-ui/core";
import { Map, X } from "@geist-ui/icons"; // Add X import
import './MapSelector.css';
import mapsData from './maps.json';

const COMBAT_STATE_KEY = 'om_play_combat_state';

const MapSelector = ({ onMapSelect, position, onDragEnd, onClose }) => { // Add onClose prop
  const [books, setBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState('');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [dragPos, setDragPos] = useState(position);
  const [loadedInitially, setLoadedInitially] = useState(false);

  useEffect(() => {
    fetchMapStructure();
  }, []);

  useEffect(() => {
    if (books.length > 0 && !loadedInitially) {
      try {
        const savedState = localStorage.getItem(COMBAT_STATE_KEY);
        if (savedState) {
          const state = JSON.parse(savedState);
          
          if (state.mapSection) {
            console.log("Restoring selected map in selector:", state.mapSection.name);
            
            const bookName = findBookForSection(state.mapSection);
            
            if (bookName) {
              setSelectedBook(bookName);
              
              const book = mapsData.books.find(b => b.name === bookName);
              setSections(book?.sections || []);
              
              setSelectedSection(state.mapSection.name);
              
              setLoadedInitially(true);
            }
          }
        }
      } catch (error) {
        console.error("Error restoring map selector state:", error);
      }
    }
  }, [books, loadedInitially]);

  const findBookForSection = (section) => {
    if (!section || !section.name) return null;
    
    for (const book of mapsData.books) {
      const foundSection = book.sections.find(s => 
        s.name === section.name || s.id === section.id);
      
      if (foundSection) {
        return book.name;
      }
    }
    
    return null;
  };

  const fetchMapStructure = async () => {
    try {
      setBooks(mapsData.books.map(book => book.name));
    } catch (error) {
      console.error("Error loading map structure:", error);
    }
  };

  const handleBookChange = (value) => {
    setSelectedBook(value);
    const book = mapsData.books.find(b => b.name === value);
    setSections(book?.sections || []);
    setSelectedSection('');
  };

  const handleSectionChange = (value) => {
    setSelectedSection(value);
    const book = mapsData.books.find(b => b.name === selectedBook);
    const section = book?.sections.find(s => s.name === value);
    
    if (section) {
      // Combine tokens and players arrays
      const allTokens = [
        ...(section.tokens || []),
        ...(section.players || [])
      ];
      
      // Pass all tokens to parent
      onMapSelect(section.image, allTokens, section);
    }
  };

  // Dragging functionality
  const handleMouseDown = (e) => {
    if (e.target.classList.contains('map-selector-header')) {
      setIsDragging(true);
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDragPos(prevPos => ({
        x: prevPos.x + e.movementX,
        y: prevPos.y + e.movementY
      }));
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      onDragEnd(dragPos);
    }
  };

  return (
    <div 
      className="map-selector"
      style={{ 
        transform: `translate(${dragPos.x}px, ${dragPos.y}px)`,
        cursor: isDragging ? 'grabbing' : 'auto'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <div className="map-selector-header">
        <Map size={20} />
        <Text small>Map</Text>
        <button className="close-button" onClick={onClose}>
          <X size={20} />
        </button>
      </div>
      <div className="map-selector-content">
        <Select 
          placeholder="Select Book"
          value={selectedBook}
          onChange={handleBookChange}
          width="100%"
        >
          {books.map(book => (
            <Select.Option key={book} value={book}>{book}</Select.Option>
          ))}
        </Select>

        <Select
          placeholder="Select Map"
          value={selectedSection}
          onChange={handleSectionChange}
          width="100%"
          disabled={!selectedBook}
        >
          {sections.map(section => (
            <Select.Option key={section.name} value={section.name}>
              {section.name}
            </Select.Option>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default MapSelector;